/**
 * Ecommerce Flavor Module
 *
 * Provides the full ecommerce experience: product display, cart management,
 * variant selection, fly-to-cart animation, and store cart integration.
 *
 * Extends BaseFlavor — universal handlers (show_items, cart_updated,
 * show_media, dismiss_media) are wired automatically.
 *
 * --- add_to_store_cart vs add_to_site_cart (backend → widget) ---
 * - add_to_store_cart: Shopify only. The widget calls the store’s official AJAX Cart API
 *   (/cart/add.js, /cart.js). “Store” means the Shopify storefront session in the browser.
 * - add_to_site_cart: Custom / scraped sites (e.g. Noy Hasade). The widget runs a partner
 *   hook script that manipulates that site’s cart DOM or JS — no fixed API shape.
 * Both are “client-side cart” adds: the server cannot mutate the cart alone; it tells the
 * widget what to add, then the widget reports cart_add_result when done.
 *
 * --- cart_updated vs cart_add_result ---
 * - cart_updated (server → widget): Pushes totals / item count / optional “added” toast so
 *   the in-widget cart UI stays in sync. Used when the partner cart lives on the server
 *   (mock-store, pharmacy, etc.): after a successful server-side add, the backend sends
 *   cart_updated and may inject LLM context in the same turn. For Shopify/hook partners,
 *   the widget often updates the bar locally after Ajax; the server may still send
 *   cart_updated in some flows, but client-side adds usually end with cart_add_result only.
 * - cart_add_result (widget → server): Async result after the widget executed a client-side
 *   add (add_to_store_cart or add_to_site_cart). Carries success, totals, and optional
 *   verbalAck (true when the user tapped Add in the UI so the assistant should speak;
 *   false/omitted for agent/tool adds so the model is not told to acknowledge twice).
 *
 * @deprecated The CDN-based partner hooks path (_loadPartnerHooks and the
 * add_to_site_cart / get_site_cart / simulate_search_on_site / site_action
 * handlers) is being replaced by DB-backed per-partner adapter scripts pushed
 * over the WebSocket (see ttp.partner_adapters + run_partner_script). Kept
 * live for the current demo; delete after the next demo.
 */
import { BaseFlavor } from './BaseFlavor.js';
import { EcommerceManager } from '../ecommerce/EcommerceManager.js';
import { PartnerScriptRunner, buildRunPartnerScriptTool } from '../ecommerce/PartnerScriptRunner.js';

class EcommerceFlavor extends BaseFlavor {
  createManager({ shadowRoot, primaryColor }) {
    return new EcommerceManager({
      shadowRoot,
      primaryColor,
      onProductSelected: (product) => this._handleProductSelected(product),
      onProductDeselected: (product) => this.handleItemDeselected(product),
    });
  }

  getManagerHideMethod() {
    return 'hideProducts';
  }

  buildTools() {
    // DB-backed per-partner adapter runner. Backend pushes the bundle via
    // 'set_partner_bundle' (handled below) and invokes individual adapters
    // via the 'run_partner_script' client tool.
    if (!this._partnerScriptRunner) {
      this._partnerScriptRunner = new PartnerScriptRunner();
    }
    return [buildRunPartnerScriptTool(this._partnerScriptRunner)];
  }

  buildExtraMessageHandlers() {
    return {
      // === DB-backed per-partner adapters (replaces CDN hooks) ===
      // Backend pushes the full bundle once at session start; the widget then
      // executes individual adapters via the 'run_partner_script' client tool.
      // Shape: { t: 'partner_bundle', partner_id, platform, adapters: { action: {...} } }
      'partner_bundle': (data) => {
        this._partnerScriptRunner?.setBundle(data);
      },

      // Shopify: see file header — add_to_store_cart uses /cart/add.js + /cart.js.
      'add_to_store_cart': (data) => this._handleAddToStoreCart(data),
      'get_store_cart': () => this._handleGetStoreCart(),

      // Hook partners: add_to_site_cart delegates to partner script (e.g. hooks/noyhasade.js).
      // DEPRECATED: superseded by DB-backed partner_adapters + run_partner_script.
      // Kept for the current demo only — delete after the next demo.
      'add_to_site_cart': (data) => {
        // Shopping list: fire-and-forget unless it's the last item (which reports cart state)
        const isShoppingList = data?.showOnPage;
        const isLast = data?.last;
        const responseType = (!isShoppingList || isLast) ? 'cart_add_result' : null;
        this._handleHookCall('addToCart', data, responseType);
      },
      'get_site_cart': () => this._handleHookCall('getCart', {}, 'cart_state_result'),
      'simulate_search_on_site': (data) => this._handleHookCall('simulateSearch', data),
      // ^ Fire-and-forget: triggers site's native search UI visually, NO result sent back to LLM
      'site_action': (data) => this._handleSiteAction(data),
    };
  }

  // ─── Site actions ──────────────────────────────────────────────

  /**
   * Dispatch site-level actions (clear products, etc.) to the appropriate hook.
   */
  _handleSiteAction(data) {
    const action = data?.action;
    if (!action) return;
    const hook = this.hooks?.[action];
    if (hook) {
      hook(data);
    } else {
      console.warn(`[EcommerceFlavor] No hook for site_action: ${action}`);
    }
  }

  // ─── Hook-based partner integration ────────────────────────────

  /**
   * Load partner-specific hook script from CDN.
   * Called during flavor init — hooks are available before any messages arrive.
   *
   * @deprecated Superseded by DB-backed partner_adapters pushed over the WS
   * and executed via run_partner_script. Kept live for the current demo;
   * delete after the next demo (along with _handleHookCall, _handleSiteAction,
   * and the add_to_site_cart / get_site_cart / simulate_search_on_site /
   * site_action entries in buildExtraMessageHandlers).
   */
  async _loadPartnerHooks() {
    const partnerId = this.flavorOpts?.partnerId;
    if (!partnerId || partnerId === 'shopify' || partnerId === 'mock-store') return;

    const hooksUrl = this.flavorOpts.hooksUrl
      || `https://cdn.talktopc.com/hooks/${partnerId}.js`;

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = hooksUrl;
      script.onload = () => {
        this.hooks = window.__TTP_HOOKS__ || {};
        console.log(`[EcommerceFlavor] Loaded partner hooks from ${hooksUrl}:`, Object.keys(this.hooks));
        resolve(this.hooks);
      };
      script.onerror = () => {
        console.warn(`[EcommerceFlavor] Failed to load hooks: ${hooksUrl}`);
        this.hooks = {};
        resolve({});
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Generic hook dispatcher — calls a hook function from the loaded partner script.
   * If responseType is provided, sends the result back to the backend via VoiceSDK.
   * If responseType is null/undefined, it's fire-and-forget (e.g., simulate_search_on_site).
   * For cart_add_result, copies data.verbalAck so the server can tell UI-originated adds
   * from agent tool adds (see file header).
   */
  async _handleHookCall(hookName, data, responseType) {
    console.log(`[EcommerceFlavor] _handleHookCall: hookName=${hookName}, responseType=${responseType}, data=`, data);
    console.log(`[EcommerceFlavor] hooks loaded:`, Object.keys(this.hooks || {}));
    const hook = this.hooks?.[hookName];
    if (!hook) {
      console.error(`[EcommerceFlavor] Hook '${hookName}' NOT FOUND in hooks object`);
      if (responseType) {
        this.sendToVoiceSDK({
          t: responseType,
          success: false,
          error: `No ${hookName} hook`,
          ...(responseType === 'cart_add_result' && data?.verbalAck ? { verbalAck: true } : {}),
        });
      }
      return;
    }
    try {
      console.log(`[EcommerceFlavor] Calling hook '${hookName}'...`);
      const result = await hook(data);
      console.log(`[EcommerceFlavor] Hook '${hookName}' returned:`, JSON.stringify(result));
      if (responseType) {
        const msg = { t: responseType, success: true, ...(result || {}) };
        if (responseType === 'cart_add_result' && data?.verbalAck) {
          msg.verbalAck = true;
        }
        console.log(`[EcommerceFlavor] Sending to backend:`, JSON.stringify(msg));
        this.sendToVoiceSDK(msg);
      }
    } catch (e) {
      console.error(`[EcommerceFlavor] Hook '${hookName}' THREW:`, e);
      if (responseType) {
        this.sendToVoiceSDK({
          t: responseType,
          success: false,
          error: e.message,
          ...(responseType === 'cart_add_result' && data?.verbalAck ? { verbalAck: true } : {}),
        });
      }
    }
  }

  // ─── Ecommerce-specific handlers ──────────────────────────────

  _handleProductSelected(product) {
    const quantity = product.selectedAmount || 1;
    this.stopPlayback();
    this.sendToVoiceSDK({
      t: 'product_selected',
      productId: product.id,
      productName: product.name,
      price: product.price,
      quantity,
      sellBy: product.sellBy || 'quantity',
    });
  }

  async _handleGetStoreCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        console.error('[EcommerceFlavor] Failed to fetch /cart.js:', response.status);
        this.sendToVoiceSDK({
          t: 'cart_state_result',
          success: false,
          error: `Failed to fetch cart: HTTP ${response.status}`,
        });
        return;
      }

      const cart = await response.json();

      this.sendToVoiceSDK({
        t: 'cart_state_result',
        success: true,
        items: (cart.items || []).map(item => ({
          title: item.title,
          variant_title: item.variant_title,
          quantity: item.quantity,
          price: item.price,
          line_price: item.line_price,
          image: item.image,
          variant_id: item.variant_id,
          product_id: item.product_id,
        })),
        item_count: cart.item_count,
        total_price: cart.total_price,
        currency: cart.currency,
      });

      console.log(`[EcommerceFlavor] Cart state sent: ${cart.item_count} items, ${cart.currency} ${(cart.total_price / 100).toFixed(2)}`);
    } catch (error) {
      console.error('[EcommerceFlavor] Error fetching cart:', error);
      this.sendToVoiceSDK({
        t: 'cart_state_result',
        success: false,
        error: error.message || 'Failed to fetch cart',
      });
    }
  }

  /**
   * Shopify client-side add. Echoes verbalAck on cart_add_result when the server set it
   * (UI Add via product_selected); agent tool adds omit verbalAck.
   */
  async _handleAddToStoreCart(data) {
    const { variantId, quantity } = data;
    let productName = '';

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: quantity || 1 }],
        }),
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('[EcommerceFlavor] /cart/add.js failed:', addResponse.status, errorText);
        this.sendToVoiceSDK({
          t: 'cart_add_result',
          success: false,
          error: `Cart add failed: HTTP ${addResponse.status}`,
          productName: productName || String(variantId),
          quantity: quantity || 1,
          ...(data?.verbalAck ? { verbalAck: true } : {}),
        });
        return;
      }

      const addResult = await addResponse.json();
      if (addResult.items?.length > 0) {
        productName = addResult.items[0].title || '';
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      let cartRefreshed = false;
      try {
        cartRefreshed = this._refreshCartCount(cart.item_count);
      } catch (e) {
        console.warn('[EcommerceFlavor] Cart count refresh failed (non-critical):', e);
      }

      this.manager.handleCartUpdated({
        action: 'added',
        product: { name: productName, id: variantId },
        cartTotal: cart.total_price / 100,
        cartItemCount: cart.item_count,
        currency: cart.currency,
      });

      this.sendToVoiceSDK({
        t: 'cart_add_result',
        success: true,
        cartRefreshed,
        productName,
        quantity: quantity || 1,
        cartTotal: cart.total_price,
        cartItemCount: cart.item_count,
        currency: cart.currency,
        ...(data?.verbalAck ? { verbalAck: true } : {}),
      });

      console.log(`[EcommerceFlavor] Cart add result sent: ${productName}, refreshed=${cartRefreshed}, ${cart.item_count} items`);
    } catch (error) {
      console.error('[EcommerceFlavor] Error in handleAddToStoreCart:', error);
      this.sendToVoiceSDK({
        t: 'cart_add_result',
        success: false,
        error: error.message || 'Failed to add to cart',
        productName: productName || String(variantId),
        quantity: quantity || 1,
        ...(data?.verbalAck ? { verbalAck: true } : {}),
      });
    }
  }

  _refreshCartCount(itemCount) {
    let refreshed = false;
    const selectors = [
      '.cart-count', '.cart-count-bubble', '[data-cart-count]',
      '.js-cart-count', '.cart-item-count', '#CartCount',
      '.site-header__cart-count', '.cart-count-tag',
    ];
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.textContent = itemCount;
        refreshed = true;
      });
    });
    return refreshed;
  }

  // ─── Test API ─────────────────────────────────────────────────

  _exposeTestAPI() {
    const self = this;
    window.__TTP_ECOMMERCE__ = {
      handleMessage: (payload) => self.manager.handleMessage(payload),
      showProducts: async (query, limit = 5, sessionId = 'test') => {
        const baseUrl = window.__TTP_MOCK_API__ || 'https://backend.talktopc.com';
        try {
          const response = await fetch(
            `${baseUrl}/api/partner/mock-store/products/search?q=${encodeURIComponent(query)}&limit=${limit}&sessionId=${encodeURIComponent(sessionId)}`
          );
          const data = await response.json();
          self.manager.handleShowProducts({
            t: 'show_products',
            products: data.products,
            title: `${data.totalCount} results for "${query}"`,
            layout: data.products.length <= 4 ? 'cards' : 'list',
          });
        } catch (error) {
          console.error('[EcommerceFlavor] showProducts failed:', error);
        }
      },
      getCart: () => ({
        total: self.manager.cartTotal,
        count: self.manager.cartItemCount,
      }),
      clearCart: () => {
        self.manager.cartItemCount = 0;
        self.manager.cartTotal = 0;
        self.manager.presenter.updateCart({
          itemCount: 0,
          total: 0,
          currency: self.manager.currencySymbol,
        });
      },

      // Partner-adapter runner (DB-backed per-partner scripts).
      // Usage in devtools:
      //   __TTP_ECOMMERCE__.setPartnerBundle({
      //     partner_id: 'super-pharm',
      //     platform:   'hybris',
      //     adapters:   { 'cart.add': { version: 1, code_js: 'async (ctx) => {...}', code_sha256: '...' } },
      //   })
      //   await __TTP_ECOMMERCE__.runAdapter('cart.add', { sku: '1234', qty: 1 })
      partnerScriptRunner: () => self._partnerScriptRunner,
      setPartnerBundle: (msg) => self._partnerScriptRunner?.setBundle(msg),
      runAdapter: (action, args) => self._partnerScriptRunner?.runAdapter(action, args),
    };
  }

  destroy() {
    delete window.__TTP_ECOMMERCE__;
    super.destroy();
  }
}

export function createEcommerceFlavor(widget, flavor = {}) {
  const instance = new EcommerceFlavor(widget, flavor);
  instance._exposeTestAPI();
  // DEPRECATED: CDN hook loading — delete after next demo once partner_adapters rollout is complete.
  // Load partner hooks asynchronously (non-blocking — hooks will be ready before messages arrive)
  instance._loadPartnerHooks();
  return instance;
}
